import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import LegalClosingSection from '../components/LegalClosingSection';
import './Privacy.css';

const trustCards = [
  {
    id: 'shield',
    text: 'Your data is protected with industry-standard security',
    Icon: IconShield,
  },
  {
    id: 'lock',
    text: 'We never sell your personal information',
    Icon: IconLock,
  },
  {
    id: 'eye',
    text: 'Complete transparency in how we use your data',
    Icon: IconEye,
  },
  {
    id: 'check',
    text: 'Committed to data protection compliance',
    Icon: IconCheck,
  },
];

const accordionSections = [
  {
    id: 'understanding',
    title: 'Understanding This Privacy Policy',
    Icon: IconGlobe,
    body: (
      <>
        <p>
          Welcome. This Privacy Policy describes how <span className="privacy-brand-name">Landmarks Construction</span>{' '}
          (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) collects, uses, stores, and safeguards personal information
          when you visit our website, submit enquiries, interact with us by phone, email, messaging apps, or in person,
          or use any related services we offer (collectively, the &quot;Services&quot;).
        </p>
        <p>
          By accessing the Services or providing your information, you acknowledge that you have read this Policy and
          agree to the practices described here, together with our <Link to="/terms">Terms &amp; Conditions</Link> where
          applicable. If you do not agree, please discontinue use and do not submit personal data.
        </p>
        <p>
          We process personal information only for legitimate purposes and, where required by law, on a valid legal
          basis—such as your consent, performance of a contract or steps prior to contracting, our legitimate interests
          (balanced against your rights), or compliance with a legal obligation.
        </p>
        <p>
          Our Services are not intended for individuals under 18 years of age. We do not knowingly collect personal
          information from children. If you believe we have received such information, please contact us and we will
          take appropriate steps to delete it.
        </p>
        <p>
          We may update this Policy from time to time. When we do, we will revise the &quot;Last Updated&quot; date at
          the top of this page. We encourage you to review this Policy periodically. Continued use of the Services
          after changes are posted constitutes your acceptance of the updated Policy, where permitted by applicable law.
        </p>
      </>
    ),
  },
  {
    id: 'collected',
    title: 'Information Collected from Visitors',
    Icon: IconUser,
    body: (
      <>
        <p>
          We may collect different types of information depending on whether you are browsing our website, requesting a
          callback, discussing a project, or are already a client. In general, this may include:
        </p>
        <ul>
          <li>
            <strong>Information you provide voluntarily:</strong> such as your full name, email address, phone number,
            city or locality, plot or site location, project requirements, budget indications, preferred timeline, and any
            other details you choose to share through forms, email, chat, or during consultations.
          </li>
          <li>
            <strong>Project and transaction-related information:</strong> including drawings, specifications, contracts,
            billing details, site visit notes, correspondence, and records needed to deliver construction and related
            services.
          </li>
          <li>
            <strong>Automatically collected technical information:</strong> when you visit our website, we or our
            service providers may collect your IP address, browser type and version, device type, operating system,
            referring URL, pages viewed, time and date of access, and approximate geographic location derived from IP
            (where available).
          </li>
          <li>
            <strong>Communications metadata:</strong> such as logs of calls, SMS, or messaging (where used) to respond to
            enquiries and manage projects, subject to applicable law and your consent where required.
          </li>
        </ul>
        <p>
          We use this information to respond to your requests, prepare proposals and contracts, execute and manage
          projects, process payments, comply with regulatory requirements (including those applicable to real estate
          and construction where relevant), improve our website and services, send service-related communications and,
          where permitted, marketing (with opt-out where required), prevent fraud, and protect our legal rights.
        </p>
      </>
    ),
  },
  {
    id: 'collecting',
    title: 'Collecting Personal Information',
    Icon: IconDocument,
    body: (
      <>
        <p>
          We collect personal information through direct interactions—for example when you fill out &quot;Plan Your
          Project&quot; or contact forms, request a callback, subscribe to updates, attend a meeting, or sign agreements.
          We may also record information when you communicate with us by telephone, email, or approved messaging
          channels.
        </p>
        <p>
          When you browse our website, certain information is collected automatically through cookies, web beacons, and
          similar technologies. These tools help us recognise your device, remember preferences, analyse traffic and user
          behaviour, measure campaign effectiveness, and improve site performance and security.
        </p>
        <p>
          You can manage or disable cookies through your browser settings. Please note that blocking strictly necessary
          cookies may affect the functionality of parts of our website (for example, form submission or session
          continuity).
        </p>
        <p>
          In limited cases, we may receive information about you from third parties—such as referrals, public registers,
          or partners assisting with verification—only where permitted by law and, where required, with your knowledge
          or consent.
        </p>
      </>
    ),
  },
  {
    id: 'protect',
    title: 'How We Protect Your Personal Information',
    Icon: IconPadlock,
    body: (
      <>
        <p>
          Protecting your data is important to us. We use widely accepted technical safeguards to protect personal
          information from unauthorized access or misuse.
        </p>
        <div className="privacy-safeguards-box">
          <ul className="privacy-safeguards-list">
            <li>
              <span className="privacy-safeguard-check" aria-hidden="true">
                <svg viewBox="0 0 12 10" width="11" height="9" fill="none">
                  <path
                    d="M1 5l3.5 3.5L11 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Technical Safeguards:</strong> Industry-standard security measures to prevent unauthorized access.
              </span>
            </li>
            <li>
              <span className="privacy-safeguard-check" aria-hidden="true">
                <svg viewBox="0 0 12 10" width="11" height="9" fill="none">
                  <path
                    d="M1 5l3.5 3.5L11 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Confidential Treatment:</strong> Your information is handled with strict confidentiality
                protocols.
              </span>
            </li>
            <li>
              <span className="privacy-safeguard-check" aria-hidden="true">
                <svg viewBox="0 0 12 10" width="11" height="9" fill="none">
                  <path
                    d="M1 5l3.5 3.5L11 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Secure Storage:</strong> Data stored on protected servers with restricted access.
              </span>
            </li>
          </ul>
        </div>
        <div className="privacy-important-warning">
          <p>
            <strong>Important Note:</strong> However, no system can guarantee absolute security. While we work to
            protect your information, transmission inherently carries some risk. By using our website, you acknowledge
            this limitation.
          </p>
        </div>
        <p>
          We retain personal information only as long as needed for the purposes in this Policy and applicable law. We do{' '}
          <strong>not</strong> sell your personal information. We may share data with trusted processors under contract,
          advisers, or authorities when required. To exercise access, correction, deletion, or other rights where
          available, contact us using the details in the overview section above.
        </p>
      </>
    ),
  },
  {
    id: 'advertising',
    title: 'Advertising and Third-Party Content',
    Icon: IconChart,
    body: (
      <>
        <p>
          Our site may use cookies for analytics and to display relevant ads. Cookies are small files stored on your
          device that allow us and third parties to personalize content based on browsing patterns.
        </p>
        <div className="privacy-note-grey">
          <p>
            <span className="privacy-note-label">Important: </span>
            This Policy covers cookies used by{' '}
            <span className="privacy-brand-name">Landmarks Construction</span> only. We do not control cookies used by
            external advertisers or their practices.
          </p>
        </div>
        <p>
          Third-party tools may collect information under their own policies. Where required, we seek consent for
          non-essential cookies and work with partners who process data only as we instruct, in line with applicable
          requirements.
        </p>
      </>
    ),
  },
  {
    id: 'third-party-links',
    title: 'Third-Party Links',
    Icon: IconLinkChain,
    body: (
      <>
        <p>
          Our website may include hyperlinks to external websites, portals, maps, payment gateways, social media, or
          partner pages that are not operated by{' '}
          <span className="privacy-brand-name">Landmarks Construction</span>. These links are provided for your
          convenience only.
        </p>
        <p>
          We do not control third-party sites and are not responsible for their content, privacy policies, cookie
          practices, or security. Visiting a third-party site is at your own risk. We encourage you to review the privacy
          statement and terms of every site you visit before submitting any personal information.
        </p>
        <p>
          Inclusion of a link does not imply our endorsement of the third party or its offerings. If you have concerns
          about a linked site, please contact that site directly.
        </p>
      </>
    ),
  },
  {
    id: 'analyzing-behavior',
    title: 'Analyzing Visitor Behavior',
    Icon: IconAnalyticsBars,
    body: (
      <>
        <p>
          We may use analytics tools and similar technologies to understand how visitors use our website—for example,
          which pages are viewed, how long sessions last, approximate geographic regions (derived from IP at an aggregate
          level), referral sources, and device or browser types.
        </p>
        <p>
          This information is typically collected in aggregated or de-identified form to improve site performance,
          content, and user experience, and to measure the effectiveness of our communications. Where required by law, we
          obtain consent before enabling non-essential analytics or marketing measurement tools.
        </p>
        <p>
          You may be able to opt out of certain analytics through your browser settings, industry opt-out tools, or
          cookie preference controls where we provide them. Essential analytics needed for security or core operation may
          remain active.
        </p>
      </>
    ),
  },
  {
    id: 'cookies-personalize',
    title: 'Personalizing Your Experience with Cookies',
    Icon: IconCookie,
    body: (
      <>
        <p>
          Cookies and similar technologies (such as local storage or pixels) help us recognize your browser, maintain
          session state, remember preferences (e.g. language or form progress where applicable), and deliver a more
          relevant experience when you return to our site.
        </p>
        <p>
          We may use strictly necessary cookies that are required for the website to function, performance cookies to
          understand usage, and—where permitted—functionality or preference cookies to personalize content. Marketing or
          advertising cookies, if used, will be deployed in line with applicable law and your choices.
        </p>
        <p>
          Most browsers let you refuse or delete cookies through their settings. Blocking all cookies may prevent some
          features from working correctly. For more detail on how we handle personal data in connection with cookies, see
          the sections on collecting information and third-party tools elsewhere in this Policy.
        </p>
      </>
    ),
  },
  {
    id: 'policy-updates',
    title: 'Keeping Up with Privacy Policy Updates',
    Icon: IconEye,
    body: (
      <>
        <p>
          We may revise this Privacy Policy periodically to reflect changes in our practices, technology, legal
          requirements, or the scope of our Services. When we update the Policy, we will change the &quot;Last
          Updated&quot; date shown at the top of this page.
        </p>
        <p>
          For material changes, we may provide additional notice—such as a banner on the website or, where we have your
          contact details and the law requires it, an email or other communication—before the changes take effect.
        </p>
        <p>
          We encourage you to review this Policy regularly so you stay informed about how we protect your information.
          Your continued use of the Services after the effective date of an update constitutes your acceptance of the
          revised Policy, where permitted by applicable law.
        </p>
      </>
    ),
  },
  {
    id: 'dispute-resolution',
    title: 'Dispute Resolution and Jurisdiction',
    Icon: IconGavel,
    body: (
      <>
        <p>
          If you have a concern about how we handle your personal information, please contact us first at{' '}
          <a href="mailto:info@landmarksconstruction.in">info@landmarksconstruction.in</a> or{' '}
          <a href="tel:+919319219252">+91-93192-19252</a>. We will endeavour to address your query promptly and in good
          faith.
        </p>
        <p>
          This Policy is governed by the laws of India. Subject to mandatory provisions of law, courts located at Noida,
          Uttar Pradesh, shall have exclusive jurisdiction over any dispute arising from or related to this Privacy
          Policy or our processing of personal information through this website, except where a separate agreement with
          you specifies a different dispute mechanism for that relationship.
        </p>
        <p>
          Nothing in this section limits your right to lodge a complaint with a competent data protection or regulatory
          authority where such a right exists under applicable law.
        </p>
        <p>
          For broader contractual or commercial disputes, our <Link to="/terms">Terms &amp; Conditions</Link> may
          contain additional provisions; please refer to them where relevant.
        </p>
      </>
    ),
  },
];

function IconShield({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M12 3L5 6v5c0 4.5 2.8 8.5 7 10 4.2-1.5 7-5.5 7-10V6l-7-3z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconLock({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8 11V8a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function IconEye({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function IconCheck({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconGlobe({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 12h18M12 3a14 14 0 000 18M12 3a14 14 0 010 18"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconUser({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M6 20v-1c0-2.5 2-4.5 6-4.5s6 2 6 4.5v1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconDocument({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M14 2H7a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V7l-5-5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M14 2v5h5M9 13h6M9 17h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconPadlock({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <rect x="6" y="10" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 10V7a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconChart({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path d="M4 20V10M10 20V4M16 20v-6M22 20V12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconDocOverview({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M13 2H7a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V8l-6-6z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <path d="M13 2v6h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" />
    </svg>
  );
}

function IconLinkChain({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M9 17H7A5 5 0 017 7h2M15 7h2a5 5 0 010 10h-2M8 12h8"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconAnalyticsBars({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path d="M6 20V12M12 20V8M18 20V4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function IconCookie({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M12 4a8 8 0 108 8c0-.3 0-.7-.1-1h-2a2 2 0 01-2-2V7.1A8 8 0 0012 4z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="11" r="1.1" fill="currentColor" />
      <circle cx="13" cy="10" r="1.1" fill="currentColor" />
      <circle cx="11" cy="14" r="1.1" fill="currentColor" />
      <circle cx="15" cy="14" r="1.1" fill="currentColor" />
    </svg>
  );
}

function IconGavel({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M14 13l-8 8M4 20h4M9.5 7.5L16 14M19 11l-3-3-5.5 5.5 3 3L19 11zM3 21l2-2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Privacy() {
  const baseId = useId();
  const [openKey, setOpenKey] = useState(null);

  const toggle = (key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  return (
    <div className="privacy-page">
      <header className="privacy-hero">
        <div className="privacy-hero-inner">
          <div className="privacy-hero-badge" aria-hidden="true">
            <IconShield className="privacy-hero-badge-icon" />
          </div>
          <h1 className="privacy-hero-title">Privacy Policy</h1>
          <p className="privacy-hero-tagline">Your privacy is our priority. Learn how we protect your data.</p>
          <p className="privacy-hero-date">Last Updated: January 11, 2026</p>
        </div>
      </header>

      <div className="privacy-trust-strip">
        <div className="page-wrap privacy-main-inner privacy-trust-inner">
          <ul className="privacy-trust-grid">
            {trustCards.map(({ id, text, Icon }) => (
              <li key={id} className="privacy-trust-card">
                <span className="privacy-trust-card-icon" aria-hidden>
                  <Icon className="privacy-trust-svg" />
                </span>
                <p className="privacy-trust-card-text">{text}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="privacy-main">
        <div className="page-wrap privacy-main-inner">
          <section className="privacy-overview" aria-labelledby="privacy-overview-heading">
            <h2 id="privacy-overview-heading" className="privacy-overview-title">
              <span className="privacy-overview-icon-wrap" aria-hidden="true">
                <IconDocOverview />
              </span>
              An Overview
            </h2>
            <div className="privacy-overview-prose">
              <p>
                Welcome to the Privacy Policy of{' '}
                <span className="privacy-brand-name">Landmarks Construction</span>. We are committed to protecting
                your personal information and being transparent about how we handle it. This page describes what we
                collect, why we collect it, and the choices you have.
              </p>
              <p>
                Our registered office is at <strong>G-35, Floor 1, Sector 3, Noida – 201303, Uttar Pradesh, India</strong>
                . For privacy requests, email{' '}
                <a href="mailto:info@landmarksconstruction.in">info@landmarksconstruction.in</a> or call{' '}
                <a href="tel:+919319219252">+91-93192-19252</a>. You can also return to <Link to="/">Home</Link> or{' '}
                <Link to="/contact">Contact</Link> anytime.
              </p>
            </div>
          </section>

          <div className="privacy-accordions" role="list">
            {accordionSections.map((section) => {
              const panelId = `${baseId}-${section.id}`;
              const isOpen = openKey === section.id;
              const { Icon } = section;
              return (
                <div key={section.id} className="privacy-acc-item" role="listitem">
                  <button
                    type="button"
                    id={`${panelId}-btn`}
                    className="privacy-acc-trigger"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(section.id)}
                  >
                    <span className="privacy-acc-trigger-icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <span className="privacy-acc-trigger-title">{section.title}</span>
                    <span className={`privacy-acc-chevron${isOpen ? ' is-open' : ''}`} aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                        <path
                          d="M6 9l6 6 6-6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={`${panelId}-btn`}
                    className="privacy-acc-panel"
                    hidden={!isOpen}
                  >
                    <div className="privacy-acc-panel-inner">{section.body}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <LegalClosingSection variant="privacy" />
        </div>
      </div>
    </div>
  );
}
